import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, SkipBack, SkipForward, Play, Pause, Clock, Share2, Heart, Volume2, SlidersHorizontal, Radio, Airplay } from 'lucide-react';
import { MarqueeText } from '@/components/MarqueeText';
import { useAudioStore } from '@/hooks/useAudioStore';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { ShareToast } from '@/components/ShareToast';
import { EqualizerModal } from '@/components/EqualizerModal';
import { TimerModal } from '@/components/TimerModal';
import { WaveformVisualizer } from '@/components/WaveformVisualizer';
import { shareStation, type ShareResult } from '@/utils/share';
import { fetchStreams } from '@/services/strapi';
import { getInitialStations } from '@/constants/stations';
import { getStreamColor, getSolidColor } from '@/utils/streamColor';
import { useLiveStatus } from '@/hooks/useLiveStatus';
import { maybeTransliterate } from '@/utils/transliterate';
import { getLocalizedStationName } from '@/utils/stationName';
import { isMobileApp, mobileBridge } from '@/utils/mobileBridge';
import type { RadioStation } from '@/types';

export const PlayerScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'de';
  const [streams, setStreams] = useState<RadioStation[]>(() => getInitialStations());
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [showEqModal, setShowEqModal] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<ShareResult | null>(null);
  const {
    currentStation,
    currentTrack,
    isPlaying,
    volume,
    favorites,
    addFavorite,
    removeFavorite,
    sleepTimer,
    setSleepTimer,
    clearSleepTimer,
  } = useAudioStore();
  const {
    togglePlayPause,
    setVolume,
    playPrevStation,
    playNextStation,
  } = useAudioPlayer();

  useEffect(() => {
    fetchStreams(locale).then((s) => s.length > 0 && setStreams(s));
  }, [locale]);

  const isFavorite = currentStation ? favorites.includes(currentStation.id) : false;
  const stationColor = currentStation ? getStreamColor(currentStation.color, currentStation.name) : '#3b82f6';
  const stationSolidColor = getSolidColor(stationColor);
  // AirPlay dugme samo kad native Build 10+ injektira __SUTON_NATIVE__ (iOS).
  // UA fallback bi pokazao dugme i na Build 9 gdje AIRPLAY_PICKER ne postoji.
  const showAirplayButton =
    isMobileApp() &&
    typeof window !== 'undefined' &&
    window.__SUTON_NATIVE__?.platform === 'ios';

  const liveStatus = useLiveStatus();
  const isLiveStation =
    Boolean(currentStation?.streamUrl?.includes('suton_radio')) ||
    Boolean(currentStation?.name?.toLowerCase().includes('live'));
  const showLiveInfo = isLiveStation && Boolean(liveStatus?.isLive);
  const showAutoDjBanner = isLiveStation && liveStatus != null && !liveStatus.isLive;

  const localizedStationName = currentStation
    ? getLocalizedStationName(currentStation, locale)
    : '';
  const localizedDescription = maybeTransliterate(currentStation?.description, locale);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Locale za datum/vrijeme – mapiranje i18n kodova na Intl (BCP 47)
  const dateLocale = (() => {
    const map: Record<string, string> = {
      'sr-Cyrl': 'sr-Cyrl-RS',
      sr: 'sr-Latn-RS',
      mk: 'mk-MK',
      bs: 'bs-BA',
      hr: 'hr-HR',
    };
    return map[locale] || locale;
  })();

  const handleFavoriteToggle = () => {
    if (currentStation) {
      if (isFavorite) removeFavorite(currentStation.id);
      else addFavorite(currentStation.id);
    }
  };

  const handlePrev = () => playPrevStation(streams);
  const handleNext = () => playNextStation(streams);
  const handleTimerClick = () => setShowTimerModal(true);
  const handleEqClick = () => setShowEqModal(true);
  const handleAirplayClick = () => {
    mobileBridge.airplayPicker();
  };
  const handleShareClick = async () => {
    if (!currentStation) return;
    const result = await shareStation(currentStation.name, currentStation.description);
    setShareFeedback(result);
  };

  // Ako nema stanice, preusmjeri na početnu
  useEffect(() => {
    if (!currentStation) {
      navigate('/app', { replace: true });
    }
  }, [currentStation, navigate]);

  if (!currentStation) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-primary-bg overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage: `url(${currentStation.artwork})`,
          filter: 'blur(40px)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-bg/80 via-primary-bg/90 to-primary-bg" />

      {/* Back button — absolutno pozicioniran da ne zauzima flex prostor */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-10 left-4 z-20 w-12 h-12 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
        aria-label="Back"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* Main content - scrollabilan */}
      <div className="relative z-10 flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center px-6 py-4">
        {/* Artwork */}
        <div className="mb-8 flex justify-center">
          <div className={`rounded-full ${showLiveInfo ? 'p-[3px] bg-red-500 shadow-[0_0_20px_rgba(239,68,68,0.45)] animate-pulse' : ''}`}>
            <motion.div
              animate={isPlaying ? { rotate: 360 } : { rotate: 0 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              className="flex-shrink-0 w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 aspect-square rounded-full overflow-hidden border-4 shadow-2xl flex items-center justify-center"
              style={{
                borderColor: `${stationSolidColor}80`,
                ...(currentStation.artwork && currentStation.artwork.startsWith('http')
                  ? { background: 'transparent' }
                  : { background: stationColor }),
              }}
            >
              {currentStation.artwork && currentStation.artwork.startsWith('http') ? (
                <img
                  src={currentStation.artwork}
                  alt={currentStation.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Radio className="w-24 h-24 text-white drop-shadow-lg" />
              )}
            </motion.div>
          </div>
        </div>

        {isPlaying && (
          <div className="mb-4">
            <WaveformVisualizer />
          </div>
        )}

        {/* Station info */}
        <p className="text-sm font-semibold uppercase tracking-wider mb-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-1">
          {showLiveInfo && (
            <span className="inline-flex items-center gap-1.5 flex-shrink-0">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
              <span className="text-red-500 font-bold normal-case">{t('player.live')}</span>
              <span className="text-text-muted font-normal">•</span>
            </span>
          )}
        {showLiveInfo ? (
          <span className="bg-gradient-to-r from-fuchsia-500 via-violet-500 to-indigo-500 bg-clip-text text-transparent">
            {localizedStationName}
          </span>
        ) : (
          <span style={{ color: stationSolidColor }}>{localizedStationName}</span>
        )}
      </p>
        {/* R7.1: MarqueeText umjesto wrap-ujućeg <h2> da krug logoa iznad ostane stabilan
            i layout ispod ne skače kad je naziv pjesme dugačak */}
        <div className="text-center mb-1 px-2 h-7 flex items-center justify-center w-full max-w-md">
          <MarqueeText
            className="text-xl font-bold leading-tight uppercase"
            text={(() => {
              if (showLiveInfo && liveStatus?.nowPlaying) {
                const np = liveStatus.nowPlaying;
                if (np.artist) {
                  return `${maybeTransliterate(np.artist, locale)} - ${maybeTransliterate(np.title, locale)}`;
                }
                return maybeTransliterate(np.text || np.title, locale) ?? localizedStationName;
              }
              if (currentTrack && (currentTrack.artist || currentTrack.title)) {
                const sep = currentTrack.artist && currentTrack.title ? ' - ' : '';
                return `${maybeTransliterate(currentTrack.artist, locale)}${sep}${maybeTransliterate(currentTrack.title, locale)}`;
              }
              return localizedStationName;
            })()}
          />
        </div>
        <p className="text-text-secondary text-sm mb-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 text-center">
          {showLiveInfo ? (
            <span className="font-medium">
              {liveStatus?.streamerName?.trim()
                ? maybeTransliterate(liveStatus.streamerName.trim(), locale)
                : t('player.liveDj')}
            </span>
          ) : showAutoDjBanner ? (
            <span className="font-medium">
              {t('player.autoDj')} <span className="opacity-50 mx-1">•</span> {t('player.stationName')}
            </span>
          ) : localizedDescription ? (
            <span className="line-clamp-2">{localizedDescription}</span>
          ) : (
            t('home.soundOfBalkans')
          )}
        </p>

        {/* Volume slider */}
        <div className="w-full max-w-xs mb-8">
          <div className="flex items-center gap-3">
            <Volume2 className="w-5 h-5 text-text-muted flex-shrink-0" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="flex-1 h-2 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, ${stationSolidColor} 0%, ${stationSolidColor} ${volume * 100}%, rgba(255,255,255,0.2) ${volume * 100}%)`,
              }}
            />
            <span className="text-sm text-text-muted w-8">
              {Math.round(volume * 100)}%
            </span>
          </div>
        </div>

        {/* Playback controls */}
        <div className="flex items-center justify-center gap-4 sm:gap-8 mb-8">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrev}
            className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 aspect-square rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <SkipBack className="w-5 h-5 sm:w-6 sm:h-6" />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={togglePlayPause}
            className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 aspect-square rounded-full flex items-center justify-center transition-all duration-300"
            style={{
              background: stationColor,
              boxShadow: `0 10px 40px ${stationSolidColor}50`,
            }}
          >
            {isPlaying ? (
              <Pause className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            ) : (
              <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="white" />
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNext}
            className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 aspect-square rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <SkipForward className="w-5 h-5 sm:w-6 sm:h-6" />
          </motion.button>
        </div>
      </div>

      {/* Datum i vrijeme + Quick actions - fiksirano na dnu */}
      <div className="relative z-10 flex-shrink-0 flex flex-col items-center gap-3 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="flex flex-col items-center gap-0.5">
          <p className="text-xl font-semibold text-white">
            {now.toLocaleTimeString(dateLocale, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
          <p className="text-sm text-text-muted">
            {now.toLocaleDateString(dateLocale, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>
        <div className="flex justify-center gap-6">
        <button
          onClick={handleFavoriteToggle}
          className={`flex flex-col items-center gap-1 p-3 rounded-xl glass hover:bg-white/10 transition-colors ${
            isFavorite ? 'text-accent-pink' : ''
          }`}
        >
          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
          <span className="text-xs">{t('home.favorites')}</span>
        </button>
        <button onClick={handleTimerClick} className="flex flex-col items-center gap-1 p-3 rounded-xl glass hover:bg-white/10 transition-colors">
          <Clock className="w-5 h-5" />
          <span className="text-xs">
            {sleepTimer.isActive ? formatTime(sleepTimer.remainingSeconds) : t('player.timer')}
          </span>
        </button>
        <button onClick={handleShareClick} className="flex flex-col items-center gap-1 p-3 rounded-xl glass hover:bg-white/10 transition-colors">
            <Share2 className="w-5 h-5" />
            <span className="text-xs">{t('player.share')}</span>
          </button>
          <button onClick={handleEqClick} className="flex flex-col items-center gap-1 p-3 rounded-xl glass hover:bg-white/10 transition-colors">
            <SlidersHorizontal className="w-5 h-5" />
            <span className="text-xs">{t('player.equalizer')}</span>
          </button>
          {showAirplayButton && (
            <button
              onClick={handleAirplayClick}
              className="flex flex-col items-center gap-1 p-3 rounded-xl glass hover:bg-white/10 transition-colors"
            >
              <Airplay className="w-5 h-5" />
              <span className="text-xs">{t('player.airplay')}</span>
            </button>
          )}
        </div>
      </div>

      <TimerModal
        isOpen={showTimerModal}
        onClose={() => setShowTimerModal(false)}
        onSelect={setSleepTimer}
        onCancel={clearSleepTimer}
        isActive={sleepTimer.isActive}
        remainingSeconds={sleepTimer.remainingSeconds}
      />
      <EqualizerModal isOpen={showEqModal} onClose={() => setShowEqModal(false)} />
      <ShareToast result={shareFeedback} onDismiss={() => setShareFeedback(null)} />
    </div>
  );
};
